import React, { useEffect, useState } from 'react';
import { MdAdd, MdClose, MdDeleteOutline, MdEdit } from 'react-icons/md';
import {
    type ChoiceGroups,
    type FillAppValues,
    type CoursesGradesValue,
    type CGTranscript,
    type CGCourse,
    CG_SCHEDULE_COLUMNS,
    DEFAULT_CYCLE_YEAR,
    parseCoursesGrades,
} from 'global/fillapp';
import { CG_SCALES, CG_GRADE_OPTIONS } from 'global/coursesGradesData';

import './index.css';

// Transcript-entry widget mirroring the Common App "Courses & Grades" dialog
// (ca-courses-and-grades): an "Add <grade>" button opening a modal with one or
// more transcript blocks, each holding school/year/scale/schedule and course
// rows whose grade/credit columns follow the transcript's schedule.

type Props = {
    qid: string;
    gradeLabel: string;          // "9th grade" | "10th grade" | ... | "other"
    cycleYear?: number;          // drives the school-year options
    value: unknown;
    values: FillAppValues;       // for the school-name options
    choiceGroups: ChoiceGroups;
    readOnly?: boolean;
    onChange?: (qid: string, value: unknown) => void;
    onBlur?: (qid: string) => void;
};

const SCHEDULES: { code: string; label: string }[] = [
    { code: 'semesters', label: 'Semesters' },
    { code: 'trimesters', label: 'Trimesters' },
    { code: 'quarters', label: 'Quarters' },
    { code: 'yearly', label: 'Yearly' },
    { code: 'other', label: 'Other' },
];
const OTHER_SCHOOL = '__other__';

function emptyCourse(): CGCourse {
    return { subject: '', name: '', level: '', grades: {}, credits: {}, creditsNA: false };
}

function emptyTranscript(): CGTranscript {
    return {
        school: '', year: '', scale: '', scaleOther: '',
        schedule: '', scheduleOther: '', courses: [emptyCourse()],
    };
}

function columnsOf(t: CGTranscript): string[] {
    return CG_SCHEDULE_COLUMNS[t.schedule] ?? [];
}

function courseIsEmpty(c: CGCourse): boolean {
    return !c.subject && !c.name && !c.level && !c.creditsNA
        && Object.values(c.grades).every(g => !g)
        && Object.values(c.credits).every(g => !g);
}

function transcriptIsEmpty(t: CGTranscript): boolean {
    return !t.school && !t.year && !t.scale && !t.schedule
        && t.courses.every(courseIsEmpty);
}

function courseIsComplete(c: CGCourse, cols: string[]): boolean {
    if (!c.subject || !c.name || !c.level || cols.length === 0) return false;
    if (!cols.every(col => (c.grades[col] || '').trim() !== '')) return false;
    return c.creditsNA || cols.every(col => (c.credits[col] || '').trim() !== '');
}

// School options from the student's own Education answers.
function schoolOptions(values: FillAppValues): string[] {
    const qids = ['q_245', 'q_256', 'q_259', 'q_262'];
    const out: string[] = [];
    for (const q of qids) {
        const v = values[q];
        if (typeof v === 'string' && v.trim() !== '' && !out.includes(v.trim())) {
            out.push(v.trim());
        }
    }
    return out;
}

function schoolYearOptions(cycle: number): string[] {
    const out: string[] = [];
    for (let y = cycle - 5; y <= cycle; y++) out.push(`${y}-${y + 1}`);
    return out;
}

function CoursesGradesWidget({
    // Callers pass the student's real cycle; DEFAULT_CYCLE_YEAR is the single
    // rollover constant, so a missed wiring never silently freezes on a stale
    // literal buried in this file.
    qid, gradeLabel, cycleYear = DEFAULT_CYCLE_YEAR,
    value, values, choiceGroups, readOnly, onChange, onBlur,
}: Props) {
    const [open, setOpen] = useState(false);
    const data = parseCoursesGrades(value);
    const buttonLabel = gradeLabel === 'other' ? 'Add other courses' : `Add ${gradeLabel}`;
    const titleLabel = gradeLabel === 'other' ? 'other courses' : gradeLabel;

    useEffect(() => {
        if (!open) return undefined;
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = prev; };
    }, [open]);

    const commit = (next: CoursesGradesValue | '') => {
        if (onChange) onChange(qid, next);
    };

    const update = (fn: (draft: CoursesGradesValue) => void) => {
        const draft: CoursesGradesValue = data
            ? JSON.parse(JSON.stringify(data))
            : { transcripts: [emptyTranscript()] };
        fn(draft);
        commit(draft);
    };

    const openDialog = () => {
        if (!data) commit({ transcripts: [emptyTranscript()] });
        setOpen(true);
    };

    const closeDialog = () => {
        // Don't leave an all-empty object behind — it would count as progress.
        if (data && data.transcripts.every(transcriptIsEmpty)) commit('');
        setOpen(false);
        if (onBlur) onBlur(qid);
    };

    const subjects = choiceGroups['Subject'] || [];
    const levels = choiceGroups['Course Level'] || [];
    const schools = schoolOptions(values);
    const years = schoolYearOptions(cycleYear);

    // ---- summary (collapsed state) ----
    const allCols = (t: CGTranscript) => columnsOf(t);
    const completed = data
        ? data.transcripts.reduce(
            (n, t) => n + t.courses.filter(c => courseIsComplete(c, allCols(t))).length, 0)
        : 0;
    const incomplete = data
        ? data.transcripts.reduce(
            (n, t) => n + t.courses.filter(c => !courseIsEmpty(c) && !courseIsComplete(c, allCols(t))).length, 0)
        : 0;

    return (
        <div className='cgw'>
            {!data || data.transcripts.every(transcriptIsEmpty) ? (
                !readOnly && (
                    <button type='button' className='cgw__addBtn' onClick={openDialog}>
                        <MdAdd /> {buttonLabel}
                    </button>
                )
            ) : (
                <div className='cgw__summary'>
                    {data.transcripts.map((t, i) => (
                        <div key={i} className='cgw__summaryRow'>
                            <span className='cgw__summarySchool'>
                                {t.school || `Transcript #${i + 1}`}
                            </span>
                            {t.year && <span className='cgw__summaryMeta'>{t.year}</span>}
                            <span className='cgw__summaryMeta'>
                                {t.courses.filter(c => !courseIsEmpty(c)).length} course(s)
                            </span>
                        </div>
                    ))}
                    <div className='cgw__counters'>
                        <span className='cgw__counter cgw__counter--ok'>
                            Completed courses entered: {completed}
                        </span>
                        {incomplete > 0 && (
                            <span className='cgw__counter cgw__counter--warn'>
                                Incomplete courses entered: {incomplete}
                            </span>
                        )}
                    </div>
                    {!readOnly && (
                        <button type='button' className='cgw__addBtn' onClick={() => setOpen(true)}>
                            <MdEdit /> Edit {titleLabel}
                        </button>
                    )}
                </div>
            )}

            {open && !readOnly && data && (
                <div className='cgw__overlay' role='dialog' aria-modal='true'>
                    <div className='cgw__panel'>
                        <div className='cgw__head'>
                            <h3 className='cgw__title'>Courses &amp; Grades – {titleLabel}</h3>
                            <button
                                type='button'
                                className='cgw__close'
                                aria-label='Close dialog'
                                onClick={closeDialog}
                            >
                                <MdClose />
                            </button>
                        </div>
                        <div className='cgw__body'>
                            <p className='cgw__intro'>
                                Use this grid to report your courses exactly as they appear on your transcript.
                            </p>
                            <p className='cgw__intro cgw__intro--muted'>
                                If any middle school courses appear on your transcript, you do not need to
                                note the middle school's name. Please use the high school name where you
                                received credit instead.
                            </p>

                            {data.transcripts.map((t, ti) => {
                                const cols = columnsOf(t);
                                // Grades/credits reveal once a grading scale is chosen; the
                                // columns come from the schedule, defaulting to a single Final
                                // column until a schedule is also selected.
                                const displayCols = cols.length ? cols : ['Final'];
                                return (
                                    <div key={ti} className='cgw__transcript'>
                                        <div className='cgw__transcriptHead'>
                                            <h4 className='cgw__transcriptTitle'>Transcript #{ti + 1}</h4>
                                            {ti > 0 && (
                                                <button
                                                    type='button'
                                                    className='cgw__iconBtn cgw__iconBtn--danger'
                                                    aria-label={`Remove transcript ${ti + 1}`}
                                                    onClick={() => update(d => { d.transcripts.splice(ti, 1); })}
                                                >
                                                    <MdDeleteOutline />
                                                </button>
                                            )}
                                        </div>

                                        <div className='cgw__transcriptInfo'>
                                            <label className='cgw__fieldCol'>
                                                <span className='cgw__fieldLabel'>School Name <em className='cgw__req'>*</em></span>
                                                {schools.length > 0 && (t.school === '' || schools.includes(t.school)) ? (
                                                    // Schools the student listed in Education; picking
                                                    // "Other school…" switches to free text (school=' ').
                                                    <select
                                                        className='cgw__select'
                                                        value={t.school}
                                                        onChange={e => update(d => {
                                                            d.transcripts[ti].school =
                                                                e.target.value === OTHER_SCHOOL ? ' ' : e.target.value;
                                                        })}
                                                    >
                                                        <option value=''>Select</option>
                                                        {schools.map(s => <option key={s} value={s}>{s}</option>)}
                                                        <option value={OTHER_SCHOOL}>Other school…</option>
                                                    </select>
                                                ) : (
                                                    <input
                                                        className='cgw__input'
                                                        type='text'
                                                        placeholder='School name'
                                                        value={t.school.trim() === '' ? '' : t.school}
                                                        onChange={e => update(d => { d.transcripts[ti].school = e.target.value; })}
                                                    />
                                                )}
                                            </label>
                                            <label className='cgw__fieldCol'>
                                                <span className='cgw__fieldLabel'>School Year <em className='cgw__req'>*</em></span>
                                                <select
                                                    className='cgw__select'
                                                    value={t.year}
                                                    onChange={e => update(d => { d.transcripts[ti].year = e.target.value; })}
                                                >
                                                    <option value=''>Select</option>
                                                    {years.map(y => <option key={y} value={y}>{y}</option>)}
                                                </select>
                                            </label>
                                            <label className='cgw__fieldCol'>
                                                <span className='cgw__fieldLabel'>Grading Scale <em className='cgw__req'>*</em></span>
                                                <select
                                                    className='cgw__select'
                                                    value={t.scale}
                                                    onChange={e => update(d => {
                                                        // Changing scale invalidates grade selections from the
                                                        // old scale's option list — clear grade cells.
                                                        d.transcripts[ti].scale = e.target.value;
                                                        d.transcripts[ti].courses.forEach(c => { c.grades = {}; });
                                                    })}
                                                >
                                                    <option value=''>Select</option>
                                                    {CG_SCALES.map(s => <option key={s.code} value={s.code}>{s.label}</option>)}
                                                </select>
                                            </label>
                                            <label className='cgw__fieldCol'>
                                                <span className='cgw__fieldLabel'>Schedule <em className='cgw__req'>*</em></span>
                                                <select
                                                    className='cgw__select'
                                                    value={t.schedule}
                                                    onChange={e => update(d => {
                                                        const tr = d.transcripts[ti];
                                                        tr.schedule = e.target.value;
                                                        // Re-key cells: keep values for columns that
                                                        // still exist, drop the rest.
                                                        const keep = CG_SCHEDULE_COLUMNS[tr.schedule] ?? [];
                                                        tr.courses.forEach(c => {
                                                            c.grades = Object.fromEntries(
                                                                keep.filter(k => c.grades[k]).map(k => [k, c.grades[k]]));
                                                            c.credits = Object.fromEntries(
                                                                keep.filter(k => c.credits[k]).map(k => [k, c.credits[k]]));
                                                        });
                                                    })}
                                                >
                                                    <option value=''>Select</option>
                                                    {SCHEDULES.map(s => (
                                                        <option key={s.code} value={s.code}>{s.label}</option>
                                                    ))}
                                                </select>
                                                {t.schedule === 'other' && (
                                                    <input
                                                        className='cgw__input'
                                                        type='text'
                                                        placeholder='Describe schedule'
                                                        value={t.scheduleOther}
                                                        onChange={e => update(d => { d.transcripts[ti].scheduleOther = e.target.value; })}
                                                    />
                                                )}
                                            </label>
                                        </div>

                                        {t.courses.map((c, ci) => (
                                            <div key={ci} className='cgw__course'>
                                                <div className='cgw__courseInfo'>
                                                    <label className='cgw__fieldCol'>
                                                        <span className='cgw__fieldLabel'>Subject <em className='cgw__req'>*</em></span>
                                                        <select
                                                            className='cgw__select'
                                                            value={c.subject}
                                                            onChange={e => update(d => { d.transcripts[ti].courses[ci].subject = e.target.value; })}
                                                        >
                                                            <option value=''>Select</option>
                                                            {subjects.map((o, i) => (
                                                                <option key={i} value={String(o.exportCode)}>{o.label}</option>
                                                            ))}
                                                        </select>
                                                    </label>
                                                    <label className='cgw__fieldCol cgw__fieldCol--grow'>
                                                        <span className='cgw__fieldLabel'>Course Name <em className='cgw__req'>*</em></span>
                                                        <input
                                                            className='cgw__input'
                                                            type='text'
                                                            maxLength={100}
                                                            value={c.name}
                                                            onChange={e => update(d => { d.transcripts[ti].courses[ci].name = e.target.value; })}
                                                        />
                                                    </label>
                                                    <label className='cgw__fieldCol'>
                                                        <span className='cgw__fieldLabel'>Course Level <em className='cgw__req'>*</em></span>
                                                        <select
                                                            className='cgw__select'
                                                            value={c.level}
                                                            onChange={e => update(d => { d.transcripts[ti].courses[ci].level = e.target.value; })}
                                                        >
                                                            <option value=''>Select</option>
                                                            {levels.map((o, i) => (
                                                                <option key={i} value={String(o.exportCode)}>{o.label}</option>
                                                            ))}
                                                        </select>
                                                    </label>
                                                    <button
                                                        type='button'
                                                        className='cgw__iconBtn cgw__iconBtn--danger cgw__courseRemove'
                                                        aria-label='Remove course'
                                                        disabled={t.courses.length === 1}
                                                        onClick={() => update(d => { d.transcripts[ti].courses.splice(ci, 1); })}
                                                    >
                                                        <MdDeleteOutline />
                                                    </button>
                                                </div>
                                                {t.scale !== '' && (
                                                    <div className='cgw__cells'>
                                                        <div className='cgw__cellGroup'>
                                                            <span className='cgw__cellGroupLabel'>Grades <em className='cgw__req'>*</em></span>
                                                            <div className='cgw__cellRow'>
                                                                {displayCols.map(col => {
                                                                    const gradeOpts = CG_GRADE_OPTIONS[t.scale];
                                                                    const onGrade = (val: string) => update(d => {
                                                                        d.transcripts[ti].courses[ci].grades[col] = val;
                                                                    });
                                                                    return (
                                                                        <label key={col} className='cgw__cell'>
                                                                            <span>{col}</span>
                                                                            {gradeOpts ? (
                                                                                // Dropdown for fixed scales; free text when scale = Other.
                                                                                <select
                                                                                    className='cgw__select cgw__select--cell'
                                                                                    value={c.grades[col] || ''}
                                                                                    onChange={e => onGrade(e.target.value)}
                                                                                >
                                                                                    <option value=''>—</option>
                                                                                    {gradeOpts.map(g => <option key={g} value={g}>{g}</option>)}
                                                                                </select>
                                                                            ) : (
                                                                                <input
                                                                                    className='cgw__input cgw__input--cell'
                                                                                    type='text'
                                                                                    maxLength={10}
                                                                                    value={c.grades[col] || ''}
                                                                                    onChange={e => onGrade(e.target.value)}
                                                                                />
                                                                            )}
                                                                        </label>
                                                                    );
                                                                })}
                                                            </div>
                                                        </div>
                                                        <div className='cgw__cellGroup'>
                                                            <span className='cgw__cellGroupLabel'>Credits <em className='cgw__req'>*</em></span>
                                                            <div className='cgw__cellRow'>
                                                                {displayCols.map(col => (
                                                                    <label key={col} className='cgw__cell'>
                                                                        <span>{col}</span>
                                                                        <input
                                                                            className='cgw__input cgw__input--cell'
                                                                            type='text'
                                                                            maxLength={5}
                                                                            disabled={c.creditsNA}
                                                                            value={c.creditsNA ? '' : (c.credits[col] || '')}
                                                                            onChange={e => update(d => {
                                                                                d.transcripts[ti].courses[ci].credits[col] = e.target.value;
                                                                            })}
                                                                        />
                                                                    </label>
                                                                ))}
                                                                <label className='cgw__cell cgw__cell--na'>
                                                                    <span>N/A</span>
                                                                    <input
                                                                        type='checkbox'
                                                                        checked={c.creditsNA}
                                                                        onChange={e => update(d => {
                                                                            d.transcripts[ti].courses[ci].creditsNA = e.target.checked;
                                                                        })}
                                                                    />
                                                                </label>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        ))}

                                        <button
                                            type='button'
                                            className='cgw__addBtn cgw__addBtn--course'
                                            onClick={() => update(d => { d.transcripts[ti].courses.push(emptyCourse()); })}
                                        >
                                            <MdAdd /> Add another course
                                        </button>
                                    </div>
                                );
                            })}

                            <p className='cgw__intro cgw__intro--muted'>
                                Choose “Add courses from another transcript” to add courses from another
                                high school transcript, or any other courses that use a different schedule
                                or grading scale.
                            </p>
                            <button
                                type='button'
                                className='cgw__addBtn'
                                onClick={() => update(d => { d.transcripts.push(emptyTranscript()); })}
                            >
                                <MdAdd /> Add courses from another transcript
                            </button>
                        </div>
                        <div className='cgw__footer'>
                            <button type='button' className='cgw__continue' onClick={closeDialog}>
                                Continue
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default CoursesGradesWidget;
